import { isEngineeringUser, type User, useAuth } from "@assetwatch/auth";
import {
  AppShellMobileNavigationTrigger,
  AssetWatchLogo,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  DEFAULT_PROFILE_MENU_LABELS,
  Group,
  Label,
  MenuItem,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSubHeader,
  ProfileMenu,
  TopBar,
  TopBarActions,
  TopBarContent,
  TopBarLeading,
} from "@assetwatch/ui";
import type { WorkRoleCode } from "@assetwatch/work-authorization";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import {
  canAccessWebNavigationModule,
  defaultPathForRole,
} from "../../lib/access";
import { clearRetainedListState } from "../../lib/retainedListState";
import { assetIdSchema, workOrderIdSchema } from "../../lib/workIds";
import {
  buildWorkOrdersReturnSearch,
  workOrdersSearchSchema,
} from "../../lib/workOrdersSearch";
import { getWorkScopeSearch } from "../../lib/workSearch";
import { useAssetTreeQuery } from "../../queries/useAssetTreeQuery";
import { useProcedureDetailQuery } from "../../queries/useProceduresQuery";
import type { WorkFacility } from "../../queries/useWorkContextQuery";
import { useWorkOrderDetailQuery } from "../../queries/useWorkOrderDetailQuery";
import { flattenAssetTree } from "../snowflakes/assets/assetModel";
import { useWorkScope } from "../WorkScopeProvider";
import { useRetainedAssetsSearch } from "./useRetainedListState";

const workRoleLabels: Record<WorkFacility["role"], string> = {
  ADMINISTRATOR: "Administrator",
  OPERATOR: "Operator",
  REQUESTER: "Requester",
  TECHNICIAN: "Technician",
};

const simulatedWorkRoles: WorkRoleCode[] = [
  "ADMINISTRATOR",
  "TECHNICIAN",
  "OPERATOR",
  "REQUESTER",
];

function getUserInitials(user: User | null): string {
  const firstName = user?.firstName?.trim();
  const lastName = user?.lastName?.trim();

  if (firstName || lastName) {
    return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
  }

  const identity =
    user?.name?.trim() || user?.email?.split("@")[0] || user?.username;
  const parts = identity
    ?.split(/[\s._@-]+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts?.length) return "U";

  return `${parts[0][0]}${parts.length > 1 ? parts.at(-1)?.[0] : ""}`.toUpperCase();
}

function getUserRoleLabel(user: User | null): string {
  const role = user?.role.trim();

  if (!role) return "User";

  return `${role[0].toUpperCase()}${role.slice(1)}`;
}

function Brand() {
  return (
    <Group gap={8} wrap="nowrap">
      <AssetWatchLogo aria-label="AssetWatch" />
      <Label size="lg">AssetWatch</Label>
    </Group>
  );
}

function HeaderActions() {
  const { signOut, user } = useAuth();
  const {
    isNikolaTeamMember,
    selectedCustomer,
    selectedFacility,
    setSimulatedRole,
    simulatedRole,
  } = useWorkScope();
  const navigate = useNavigate();
  const isAdministrator = Boolean(
    selectedFacility &&
      canAccessWebNavigationModule(selectedFacility, "SETTINGS"),
  );
  const userName =
    user?.name?.trim() ||
    [user?.firstName?.trim(), user?.lastName?.trim()]
      .filter(Boolean)
      .join(" ") ||
    user?.email ||
    "Account";
  const scopeSearch =
    selectedCustomer && selectedFacility
      ? getWorkScopeSearch(
          {
            id: selectedCustomer.id,
            facilities: { length: selectedCustomer.facilities.length },
          },
          { id: selectedFacility.id },
        )
      : undefined;

  return (
    <ProfileMenu
      additionalContent={
        <>
          <MenuItem
            onClick={() =>
              void navigate({ search: scopeSearch, to: "/profile" })
            }
          >
            Profile
          </MenuItem>
          {isAdministrator ? (
            <MenuItem
              onClick={() =>
                void navigate({ search: scopeSearch, to: "/settings/general" })
              }
            >
              Settings
            </MenuItem>
          ) : null}
          {isNikolaTeamMember ? (
            <MenuItem
              submenu={
                <MenuRadioGroup
                  onValueChange={(value) =>
                    setSimulatedRole(value as WorkRoleCode)
                  }
                  value={simulatedRole}
                >
                  <MenuSubHeader>Simulate Work role</MenuSubHeader>
                  {simulatedWorkRoles.map((role) => (
                    <MenuRadioItem key={role} value={role}>
                      {workRoleLabels[role]}
                    </MenuRadioItem>
                  ))}
                </MenuRadioGroup>
              }
            >
              Simulate role
            </MenuItem>
          ) : null}
        </>
      }
      engineeringOnly={isEngineeringUser(user)}
      labels={{
        ...DEFAULT_PROFILE_MENU_LABELS,
        openMenuAriaLabel: `Open account menu for ${userName}`,
      }}
      onSignOut={() => {
        clearRetainedListState();
        void signOut();
      }}
    />
  );
}

export function WorkHeader() {
  const { selectedFacility, selectedCustomer } = useWorkScope();
  const routeLocation = useRouterState({
    select: (state) => ({
      params: state.matches.at(-1)?.params,
      pathname: state.location.pathname,
      search: state.location.search,
    }),
  });
  const pathname = routeLocation.pathname;
  const isAssetDetail = pathname.startsWith("/assets/");
  const isProcedureDetail =
    pathname.startsWith("/procedures/") &&
    pathname !== "/procedures/builder" &&
    pathname !== "/procedures/authoring";
  const isSettingsGeneral = pathname === "/settings/general";
  const params: Record<string, unknown> = routeLocation.params ?? {};
  const assetId = isAssetDetail
    ? assetIdSchema.safeParse(params.assetId).data
    : undefined;
  const procedureId = isProcedureDetail
    ? typeof params.procedureId === "string"
      ? params.procedureId
      : undefined
    : undefined;
  const workOrderId = pathname.startsWith("/work-orders/")
    ? workOrderIdSchema.safeParse(params.workOrderId).data
    : undefined;
  const canAccessProcedures = Boolean(
    selectedFacility &&
      canAccessWebNavigationModule(selectedFacility, "PROCEDURES"),
  );
  const canAccessWorkOrders = Boolean(
    selectedFacility &&
      canAccessWebNavigationModule(selectedFacility, "WORK_ORDERS"),
  );
  const assetTreeQuery = useAssetTreeQuery({ enabled: Boolean(assetId) });
  const procedureQuery = useProcedureDetailQuery(
    canAccessProcedures ? procedureId : undefined,
  );
  const workOrderQuery = useWorkOrderDetailQuery(
    canAccessWorkOrders ? workOrderId : undefined,
  );
  const assetRecord =
    assetId && assetTreeQuery.data
      ? flattenAssetTree(assetTreeQuery.data.nodes).find(
          (row) => row.assetId === assetId,
        )
      : undefined;
  const retainedAssetsSearch = useRetainedAssetsSearch(routeLocation.search);
  const section = pathname.startsWith("/work-orders")
    ? { label: "Work Orders", to: "/work-orders" as const }
    : pathname.startsWith("/requests")
      ? { label: "Requests", to: "/requests" as const }
      : pathname === "/assets" || isAssetDetail
        ? { label: "Assets", to: "/assets" as const }
        : pathname.startsWith("/procedures")
          ? { label: "Procedures", to: "/procedures" as const }
          : pathname.startsWith("/settings")
            ? { label: "Settings", to: "/settings/general" as const }
            : null;
  const recordIdentifier = Object.values(routeLocation.params ?? {}).find(
    (value): value is string => typeof value === "string",
  );
  const isRecordDetail =
    isAssetDetail ||
    isProcedureDetail ||
    (pathname.startsWith("/work-orders/") && pathname !== "/work-orders/");
  const currentLabel = isSettingsGeneral
    ? "General"
    : pathname === "/procedures/builder"
      ? "Procedure Builder"
      : pathname === "/profile"
        ? "Profile"
        : isAssetDetail && assetId
          ? (assetRecord?.code ??
            (assetTreeQuery.data || !assetTreeQuery.isLoading
              ? "Asset unavailable"
              : "Loading Asset…"))
          : isProcedureDetail && procedureId
            ? (procedureQuery.data?.name ??
              (procedureQuery.isLoading
                ? "Loading Procedure…"
                : "Procedure unavailable"))
            : pathname.startsWith("/work-orders/") && workOrderId
              ? workOrderQuery.data?.workOrder
                ? `${workOrderQuery.data.workOrder.number} · ${workOrderQuery.data.workOrder.title}`
                : workOrderQuery.isLoading
                  ? "Loading Work Order…"
                  : "Work Order unavailable"
              : isRecordDetail && recordIdentifier
                ? recordIdentifier
                : (section?.label ?? "Today");
  const sitePath = selectedFacility
    ? defaultPathForRole(selectedFacility)
    : "/";
  const siteSearch =
    selectedFacility && selectedCustomer
      ? getWorkScopeSearch(
          {
            id: selectedCustomer.id,
            facilities: { length: selectedCustomer.facilities.length },
          },
          { id: selectedFacility.id },
        )
      : undefined;
  const originWorkOrdersSearch = workOrdersSearchSchema.safeParse(
    routeLocation.search,
  );
  const workOrdersReturnSearch = buildWorkOrdersReturnSearch(
    originWorkOrdersSearch.success ? originWorkOrdersSearch.data : undefined,
    siteSearch ?? {},
    workOrderId,
  );

  return (
    <TopBar aria-label="Application header">
      <TopBarLeading>
        <AppShellMobileNavigationTrigger />
        <span className="max-[599px]:hidden">
          <Brand />
        </span>
      </TopBarLeading>
      <TopBarContent>
        <Breadcrumb>
          <BreadcrumbList>
            {selectedFacility && selectedCustomer ? (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    render={<Link to={sitePath} search={siteSearch} />}
                  >
                    {selectedFacility.name}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
              </>
            ) : null}
            {section && isRecordDetail ? (
              <>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    render={
                      <Link
                        to={section.to}
                        search={
                          workOrderId
                            ? workOrdersReturnSearch
                            : section.to === "/assets"
                              ? { ...siteSearch, ...retainedAssetsSearch }
                              : siteSearch
                        }
                      />
                    }
                  >
                    {section.label}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    {currentLabel}
                    {assetRecord ? (
                      <span className="sr-only">, {assetRecord.name}</span>
                    ) : null}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </>
            ) : (
              <>
                {section && isSettingsGeneral ? (
                  <>
                    <BreadcrumbItem>
                      <BreadcrumbLink
                        render={
                          <Link to="/settings/general" search={siteSearch} />
                        }
                      >
                        {section.label}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                  </>
                ) : null}
                <BreadcrumbItem>
                  <BreadcrumbPage>{currentLabel}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </TopBarContent>
      <TopBarActions>
        <HeaderActions />
      </TopBarActions>
    </TopBar>
  );
}

export { getUserInitials, getUserRoleLabel, HeaderActions };
